import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/dateFormat';
import { getApiUrl } from '../../utils/apiUrl';

const HelpDeskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const normalizeStatus = (raw) => {
    const s = String(raw || '').trim().toLowerCase();
    if (!s) return 'pending';
    if (s === 'pending') return 'pending';
    if (s === 'forward') return 'forward';
    if (s === 'in_progress' || s === 'in progress' || s === 'inprogress' || s === 'processing' || s === 'accepted') return 'in_progress';
    if (s === 'completed' || s === 'complete' || s === 'done' || s === 'resolved' || s === 'finished') return 'completed';
    if (s === 'cancelled' || s === 'canceled' || s === 'cancel' || s === 'void') return 'cancelled';
    return 'pending';
  };

  const statusToThai = useMemo(() => {
    return (status) => {
      const s = normalizeStatus(status);
      const labels = {
        pending: 'รอรับเรื่อง',
        forward: 'ส่งต่อ',
        in_progress: 'กำลังดำเนินการ',
        completed: 'ดำเนินการเสร็จสิ้น',
        cancelled: 'ยกเลิก',
      };
      return labels[s] ?? s ?? '-';
    };
  }, []);

  const statusChipSx = useMemo(() => {
    return (status) => {
      const s = normalizeStatus(status);
      if (s === 'pending') return { bgcolor: 'rgba(237, 108, 2, 0.1)', color: '#ed6c02' };
      if (s === 'forward') return { bgcolor: 'rgba(2, 119, 189, 0.1)', color: '#0277bd' };
      if (s === 'in_progress') return { bgcolor: 'rgba(132, 94, 194, 0.1)', color: '#845EC2' };
      if (s === 'completed') return { bgcolor: 'rgba(46, 125, 50, 0.1)', color: '#2e7d32' };
      if (s === 'cancelled') return { bgcolor: 'rgba(0, 0, 0, 0.06)', color: 'rgba(0, 0, 0, 0.65)' };
      return { bgcolor: 'rgba(0, 0, 0, 0.06)', color: 'rgba(0, 0, 0, 0.65)' };
    };
  }, []);

  useEffect(() => {
    if (!id) return;
    const base = getApiUrl('/api/tickets/').replace(/\/$/, '');
    if (!base) {
      setError('ไม่พบ URL API');
      setLoading(false);
      return;
    }
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    const headers = { accept: 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
    setLoading(true);
    setError(null);
    axios
      .get(`${base}/${id}/`, { headers })
      .then(({ data }) => {
        const mapped = {
          id: data?.id,
          title: data?.title,
          description: data?.description,
          category: data?.category,
          location: data?.location,
          status: normalizeStatus(data?.status),
          createdAt: data?.created_at ?? data?.createdAt,
          updatedAt: data?.updated_at ?? data?.updatedAt,
          acceptedAt: data?.accepted_at ?? data?.acceptedAt,
          acceptedBy: data?.accepted_by ?? data?.acceptedBy,
          completedAt: data?.completed_at ?? data?.completedAt,
          owner_fullname: data?.owner_fullname,
          owner_username: data?.owner_username,
          reporterName: data?.reporterName,
          attachments: Array.isArray(data?.attachments) ? data.attachments : [],
          images: Array.isArray(data?.images) ? data.images : [],
          supplementaryInfo: Array.isArray(data?.supplementaryInfo) ? data.supplementaryInfo : [],
        };
        setItem(mapped);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || err?.message || 'โหลดข้อมูลไม่สำเร็จ');
        setItem(null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const attachmentImageUrls = useMemo(() => {
    const atts = Array.isArray(item?.attachments) ? item.attachments : [];
    const urls = atts
      .map((a) => a?.file || a?.url)
      .filter(Boolean)
      .map(String)
      .filter((u) => /\.(png|jpe?g|gif|webp)(\?|$)/i.test(u));
    return urls;
  }, [item?.attachments]);

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>กำลังโหลด...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h6" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>{error}</Typography>
        <Button onClick={() => navigate(-1)} sx={{ borderRadius: 1 }}>กลับ</Button>
      </Box>
    );
  }

  if (!item) {
    return (
      <Box>
        <Typography variant="h6" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>ไม่พบรายการ</Typography>
        <Button onClick={() => navigate(-1)} sx={{ borderRadius: 1 }}>กลับ</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>รายละเอียด HelpDesk</Typography>
        <Button onClick={() => navigate(-1)} sx={{ borderRadius: 1 }}>กลับ</Button>
      </Box>

      <Card elevation={0} sx={{ bgcolor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', borderRadius: 2, boxShadow: 'none' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h5" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>{item.title}</Typography>
            <Box>
              <Chip
                label={statusToThai(item.status)}
                size="small"
                sx={{ borderRadius: '9999px', fontWeight: 600, ...statusChipSx(item.status) }}
              />
            </Box>
          </Box>

          <Box 
            sx={{ 
              mb: 1,
              display: { xs: 'block', md: 'flex' },
              flexWrap: 'wrap',
              gap: { xs: 0, md: 2 },
              alignItems: 'center'
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 0.5, md: 0 }, color: 'rgba(0, 0, 0, 0.6)' }}>
              ประเภท: {
                item.category === 'water' ? 'ประปา' : item.category === 'electricity' ? 'ไฟฟ้า' : 'สห.'
              }
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 0.5, md: 0 }, color: 'rgba(0, 0, 0, 0.6)' }}>
              สถานที่: {item.location || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 0.5, md: 0 }, color: 'rgba(0, 0, 0, 0.6)' }}>
              ผู้แจ้ง: {item.owner_fullname || item.owner_username || item.reporterName || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 0.5, md: 0 }, color: 'rgba(0, 0, 0, 0.6)' }}>
              สร้างเมื่อ: {formatDate(item.createdAt)}
            </Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* รายละเอียด: รวมรายละเอียดหลัก + รูปประกอบ + ข้อมูลเพิ่มเติม (ข้อความและรูป) ในบล็อกเดียว */}
          <Typography variant="h6" sx={{ mb: 0.5, color: 'rgba(0, 0, 0, 0.6)' }}>
            รายละเอียด
          </Typography>
          <Box sx={{ mb: 1, p: 1.5, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.08)' }}>
            <Typography variant="body1" sx={{ color: 'rgba(0, 0, 0, 0.6)', mb: 1 }}>
              {item.description}
            </Typography>

            {/* รูปแนบจาก API attachments */}
            {attachmentImageUrls.length > 0 && (
              <Box sx={{ mt: 1.5, mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.5)', fontWeight: 600, mb: 0.5, fontSize: '0.8125rem' }}>
                  รูปแนบ
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {attachmentImageUrls.map((url, i) => (
                    <Box
                      key={url}
                      component="a"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.12)',
                        '&:hover': { opacity: 0.9 }
                      }}
                    >
                      <img src={url} alt={`รูปแนบ ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* legacy: รูปจากโหมดเดิม */}
            {item.images && item.images.length > 0 && (
              <Box sx={{ mt: 1.5, mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.5)', fontWeight: 600, mb: 0.5, fontSize: '0.8125rem' }}>
                  รูปประกอบ
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {item.images.map((img, i) => (
                    <Box
                      key={i}
                      component="a"
                      href={img.dataUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.12)',
                        '&:hover': { opacity: 0.9 }
                      }}
                    >
                      <img src={img.dataUrl} alt={img.name || `รูปที่ ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {item.supplementaryInfo && item.supplementaryInfo.length > 0 && (
              item.supplementaryInfo.map((supItem, idx) => (
                <Box key={idx} sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
                  <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.55)', fontWeight: 600, mb: 0.5, fontSize: '0.8125rem' }}>
                    ข้อมูลเพิ่มเติม (โดย {supItem.addedBy} · {formatDate(supItem.addedAt)})
                  </Typography>
                  {supItem.text ? (
                    <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.8)', mb: supItem.images?.length ? 1 : 0 }}>
                      {supItem.text}
                    </Typography>
                  ) : null}
                  {supItem.images && supItem.images.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {supItem.images.map((img, i) => (
                        <Box
                          key={i}
                          component="a"
                          href={img.dataUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: '1px solid rgba(0,0,0,0.12)',
                            '&:hover': { opacity: 0.9 }
                          }}
                        >
                          <img src={img.dataUrl} alt={img.name || `รูป ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              ))
            )}
          </Box>

          <Divider sx={{ my: 1 }} />

          <Typography variant="h6" sx={{ mb: 0.5, color: 'rgba(0, 0, 0, 0.6)' }}>
            สถานะ
          </Typography>
          <List dense>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemText
                primary="สร้างเมื่อ"
                secondary={formatDate(item.createdAt)}
                primaryTypographyProps={{ sx: { color: 'rgba(0, 0, 0, 0.6)' } }}
                secondaryTypographyProps={{ sx: { color: 'rgba(0, 0, 0, 0.5)' } }}
              />
            </ListItem>
            {item.acceptedAt && (
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText
                  primary={`รับเรื่องเมื่อ (โดย: ${item.acceptedBy})`}
                  secondary={formatDate(item.acceptedAt)}
                  primaryTypographyProps={{ sx: { color: 'rgba(0, 0, 0, 0.6)' } }}
                  secondaryTypographyProps={{ sx: { color: 'rgba(0, 0, 0, 0.5)' } }}
                />
              </ListItem>
            )}
            {item.completedAt && (
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText
                  primary="เสร็จสิ้นเมื่อ"
                  secondary={formatDate(item.completedAt)}
                  primaryTypographyProps={{ sx: { color: 'rgba(0, 0, 0, 0.6)' } }}
                  secondaryTypographyProps={{ sx: { color: 'rgba(0, 0, 0, 0.5)' } }}
                />
              </ListItem>
            )}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default HelpDeskDetail;
